import React, { useState, useEffect } from "react";
import type { QuoteFormData, QuoteFormErrors, StepInfo } from "../types/quote";
import { QuoteStepper } from "../components/quote/QuoteStepper";
import { StepCustomerInfo } from "../components/quote/StepCustomerInfo";
import { StepPickupDetails } from "../components/quote/StepPickupDetails";
import { StepDeliveryDetails } from "../components/quote/StepDeliveryDetails";
import { StepProductDetails } from "../components/quote/StepProductDetails";
import { StepDeliveryPreference } from "../components/quote/StepDeliveryPreference";
import { StepConfirmation } from "../components/quote/StepConfirmation";
import { QuoteSuccess } from "../components/quote/QuoteSuccess";
import {
  FiArrowLeft,
  FiArrowRight,
  FiShield,
  FiClock,
  FiTruck,
} from "react-icons/fi";
import bgSpeed from "../assets/images/service-bg.png";
import { submitQuoteRequest } from "../services/quoteService";
import { generateOrderSummaryPdfBase64 } from "../utils/pdfGenerator";

const STEPS: StepInfo[] = [
  {
    id: 1,
    name: "Customer Information",
    shortTitle: "Customer",
    description: "Your details & company",
  },
  {
    id: 2,
    name: "Pickup Details",
    shortTitle: "Pickup",
    description: "Origin & contact person",
  },
  {
    id: 3,
    name: "Delivery Details",
    shortTitle: "Delivery",
    description: "Destination & receiver",
  },
  {
    id: 4,
    name: "Product Details",
    shortTitle: "Product",
    description: "Items, images & notes",
  },
  {
    id: 5,
    name: "Delivery Preference",
    shortTitle: "Schedule",
    description: "Date, time & handling",
  },
  {
    id: 6,
    name: "Confirmation",
    shortTitle: "Confirm",
    description: "Review & submit",
  },
];

const STORAGE_KEY = "speedyssey_quote_draft";

const initialFormData: QuoteFormData = {
  fullName: "",
  companyName: "",
  phoneNumber: "",
  email: "",
  pickupAddress: "",
  pickupContactName: "",
  pickupContactPhone: "",
  deliveryAddress: "",
  receiverName: "",
  receiverPhoneNumber: "",
  whatAreYouSending: "",
  productImages: [],
  specialDeliveryInstructions: "",
  preferredPickupDate: "",
  preferredPickupTime: "",
  specialHandling: [],
  otherHandlingDetails: "",
  additionalHandlingInstructions: "",
  customerConfirmation: false,
};

const validateEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const validateCanadianPhone = (phone: string) => {
  const normalized = phone.trim();
  if (!normalized) return false;

  return /^(?:\+?1[-.\s]?)?(?:\(?[2-9]\d{2}\)?[-.\s]?)[2-9]\d{2}[-.\s]?\d{4}$/.test(
    normalized,
  );
};

const RequestQuote: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [formData, setFormData] = useState<QuoteFormData>(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...initialFormData,
          ...parsed,
          // Images cannot be serialized with file objects easily; keep array safe
          productImages: parsed.productImages || [],
        };
      }
    } catch (e) {
      console.warn("Could not restore draft from session", e);
    }
    return initialFormData;
  });

  const [errors, setErrors] = useState<QuoteFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Set document title
  useEffect(() => {
    document.title = "Request a Custom Quote | Speedyssey Transportation";
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Save draft to sessionStorage whenever formData changes (except after successful submit)
  useEffect(() => {
    if (!isSubmitted) {
      try {
        const toSave = {
          ...formData,
          // Don't serialize File object blobs to prevent errors
          productImages: formData.productImages.map((img) => ({
            id: img.id,
            name: img.name,
            size: img.size,
            previewUrl: img.previewUrl,
          })),
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
      } catch (e) {
        console.warn("Could not save draft", e);
      }
    }
  }, [formData, isSubmitted]);

  // Update a single form field
  const handleFieldChange = (field: keyof QuoteFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (submitError) {
      setSubmitError(null);
    }
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Quick helper to duplicate customer contact to pickup contact
  const handleCopyCustomerToPickup = () => {
    setFormData((prev) => ({
      ...prev,
      pickupContactName: prev.fullName,
      pickupContactPhone: prev.phoneNumber,
    }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.pickupContactName;
      delete newErrors.pickupContactPhone;
      return newErrors;
    });
  };

  // Validate fields for a specific step
  const validateStep = (step: number): boolean => {
    const stepErrors: QuoteFormErrors = {};

    if (step === 1) {
      if (!formData.fullName.trim()) {
        stepErrors.fullName = "Full Name is required.";
      }
      if (!formData.phoneNumber.trim()) {
        stepErrors.phoneNumber = "Phone Number is required.";
      } else if (!validateCanadianPhone(formData.phoneNumber)) {
        stepErrors.phoneNumber = "Please enter a valid Canadian phone number.";
      }
      if (!formData.email.trim()) {
        stepErrors.email = "Email Address is required.";
      } else if (!validateEmail(formData.email)) {
        stepErrors.email = "Please enter a valid email address.";
      }
    } else if (step === 2) {
      if (!formData.pickupAddress.trim()) {
        stepErrors.pickupAddress = "Pickup Address is required.";
      }
      if (!formData.pickupContactName.trim()) {
        stepErrors.pickupContactName = "Pickup Contact Name is required.";
      }
      if (!formData.pickupContactPhone.trim()) {
        stepErrors.pickupContactPhone = "Pickup Contact Phone is required.";
      } else if (!validateCanadianPhone(formData.pickupContactPhone)) {
        stepErrors.pickupContactPhone =
          "Please enter a valid Canadian phone number.";
      }
    } else if (step === 3) {
      if (!formData.deliveryAddress.trim()) {
        stepErrors.deliveryAddress = "Delivery Address is required.";
      }
      if (!formData.receiverName.trim()) {
        stepErrors.receiverName = "Receiver Name is required.";
      }
      if (!formData.receiverPhoneNumber.trim()) {
        stepErrors.receiverPhoneNumber = "Receiver Phone Number is required.";
      } else if (!validateCanadianPhone(formData.receiverPhoneNumber)) {
        stepErrors.receiverPhoneNumber =
          "Please enter a valid Canadian phone number.";
      }
    } else if (step === 4) {
      if (!formData.whatAreYouSending.trim()) {
        stepErrors.whatAreYouSending = "Please tell us what you are sending.";
      }
    } else if (step === 5) {
      if (!formData.preferredPickupDate.trim()) {
        stepErrors.preferredPickupDate = "Preferred Pickup Date is required.";
      }
    } else if (step === 6) {
      if (!formData.customerConfirmation) {
        stepErrors.customerConfirmation =
          "You must confirm that the information provided is accurate and complete.";
      }
    }

    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const scrollToFormTop = () => {
    const el = document.getElementById("quote-form-container");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 300, behavior: "smooth" });
    }
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps((prev) => [...prev, currentStep]);
      }
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
      scrollToFormTop();
    }
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    scrollToFormTop();
  };

  const handleJumpToStep = (stepId: number) => {
    // Check if can jump (all prior steps must be valid or already completed)
    setCurrentStep(stepId);
    scrollToFormTop();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate all steps up to step 6
    let hasError = false;
    for (let s = 1; s <= 6; s++) {
      if (!validateStep(s)) {
        hasError = true;
        setCurrentStep(s);
        scrollToFormTop();
        break;
      }
    }

    if (hasError) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      let summaryPdfBase64: string | undefined;
      try {
        summaryPdfBase64 = generateOrderSummaryPdfBase64(formData);
      } catch (pdfErr) {
        console.warn("Could not pre-generate PDF base64:", pdfErr);
      }

      const response = await submitQuoteRequest({
        ...formData,
        summaryPdfBase64,
      });

      const generatedRef =
        response.quoteRequest?.referenceNumber ||
        `SPD-TR-${Math.floor(100000 + Math.random() * 900000)}`;
      const submissionDate = response.quoteRequest?.createdAt
        ? new Date(response.quoteRequest.createdAt).toLocaleString()
        : new Date().toLocaleString();

      setFormData((prev) => ({
        ...prev,
        referenceNumber: generatedRef,
        submittedAt: submissionDate,
        summaryPdfBase64,
      }));

      setIsSubmitting(false);
      setIsSubmitted(true);
      sessionStorage.removeItem(STORAGE_KEY);
      window.scrollTo({ top: 200, behavior: "smooth" });
    } catch (err: any) {
      console.error("Error submitting quote request to backend:", err);
      setIsSubmitting(false);
      setSubmitError(
        err?.message ||
          "Failed to submit your transportation quote request. Please check your network connection and try again.",
      );
      scrollToFormTop();
    }
  };

  const handleResetForm = () => {
    setFormData(initialFormData);
    setCompletedSteps([]);
    setCurrentStep(1);
    setSubmitError(null);
    setIsSubmitted(false);
    sessionStorage.removeItem(STORAGE_KEY);
    window.scrollTo({ top: 200, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Top Banner / Hero Header */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-24 px-4 bg-gradient-to-b from-[#151515] to-[#222222] text-white overflow-hidden">
        {/* Subtle Background Pattern */}
        <div
          className="absolute inset-0 bg-center bg-cover opacity-25 mix-blend-overlay"
          style={{ backgroundImage: `url(${bgSpeed})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent pointer-events-none" />

        <div className="container mx-auto max-w-5xl relative z-10 text-center sm:text-left">
          <div className="inline-flex items-center gap-2 bg-[#F55109]/20 border border-[#F55109]/40 text-[#F55109] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
            <FiTruck className="w-3.5 h-3.5" />
            Transportation & Logistics Request
          </div>

          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight mb-4 special-header leading-tight"
            style={{ fontFamily: "Antonio, sans-serif" }}
          >
            REQUEST A <span className="text-[#F55109]">CUSTOM QUOTE</span>
          </h1>

          {/* Exact required instructional text */}
          <p className="text-gray-300 text-base sm:text-lg max-w-3xl leading-relaxed mb-6">
            Please complete the form below to submit your transportation
            request. A Speedyssey representative will review your request and
            contact you regarding delivery arrangements.
          </p>

          {/* Trust Highlights */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-4 border-t border-white/10 max-w-3xl text-xs sm:text-sm text-gray-300">
            <div className="flex items-center justify-center sm:justify-start gap-2">
              <FiClock className="w-4 h-4 text-[#F55109]" />
              <span>Fast response within 2 hours</span>
            </div>
            <div className="flex items-center justify-center sm:justify-start gap-2">
              <FiTruck className="w-4 h-4 text-[#F55109]" />
              <span>Tailored rates for your cargo</span>
            </div>
            <div className="flex items-center justify-center sm:justify-start gap-2">
              <FiShield className="w-4 h-4 text-[#F55109]" />
              <span>Reliable fleet & live tracking</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section
        id="quote-form-container"
        className="container mx-auto max-w-4xl px-4 sm:px-6 py-8 md:py-14 -mt-8 relative z-20"
      >
        {isSubmitted ? (
          <QuoteSuccess data={formData} onReset={handleResetForm} />
        ) : (
          <div className="space-y-6">
            {/* Step Progress Bar */}
            <QuoteStepper
              steps={STEPS}
              currentStep={currentStep}
              onStepClick={handleJumpToStep}
              completedSteps={completedSteps}
            />

            {/* Step Form Card */}
            <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 p-6 sm:p-10 transition-all">
              <form
                onSubmit={
                  currentStep === 6 ? handleSubmit : (e) => e.preventDefault()
                }
                noValidate
              >
                {/* Step 1: Customer Info */}
                {currentStep === 1 && (
                  <StepCustomerInfo
                    data={formData}
                    errors={errors}
                    onChange={handleFieldChange}
                  />
                )}

                {/* Step 2: Pickup Details */}
                {currentStep === 2 && (
                  <StepPickupDetails
                    data={formData}
                    errors={errors}
                    onChange={handleFieldChange}
                    onCopyFromCustomer={handleCopyCustomerToPickup}
                  />
                )}

                {/* Step 3: Delivery Details */}
                {currentStep === 3 && (
                  <StepDeliveryDetails
                    data={formData}
                    errors={errors}
                    onChange={handleFieldChange}
                  />
                )}

                {/* Step 4: Product Details */}
                {currentStep === 4 && (
                  <StepProductDetails
                    data={formData}
                    errors={errors}
                    onChange={handleFieldChange}
                  />
                )}

                {/* Step 5: Delivery Preference */}
                {currentStep === 5 && (
                  <StepDeliveryPreference
                    data={formData}
                    errors={errors}
                    onChange={handleFieldChange}
                  />
                )}

                {/* Step 6: Confirmation */}
                {currentStep === 6 && (
                  <StepConfirmation
                    data={formData}
                    errors={errors}
                    onChange={handleFieldChange}
                    onJumpToStep={handleJumpToStep}
                    isSubmitting={isSubmitting}
                    submitError={submitError}
                    onSubmit={handleSubmit}
                  />
                )}

                {/* Navigation Buttons (Only shown for steps 1-5; Step 6 has submit inside) */}
                {currentStep < 6 && (
                  <div className="mt-10 pt-6 border-t border-gray-100 flex items-center justify-between gap-4">
                    {currentStep > 1 ? (
                      <button
                        type="button"
                        onClick={handlePrevStep}
                        className="inline-flex items-center gap-2 px-6 py-3.5 border border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50 text-gray-700 font-semibold rounded-xl transition-colors cursor-pointer"
                      >
                        <FiArrowLeft className="w-4 h-4" />
                        <span>Previous Step</span>
                      </button>
                    ) : (
                      <div />
                    )}

                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#F55109] hover:bg-[#d44304] text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 cursor-pointer ml-auto"
                    >
                      <span>Continue to Step {currentStep + 1}</span>
                      <FiArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default RequestQuote;
